import _ from 'lodash';

import {
  PlanInfoResponseDto,
  PlanLevelCostDto,
} from 'enrollment/queries/usePlanInformationQuery/utils';
import {
  DependentFormDataDto,
  BuildElectionParamsDto,
  ElectRequestDto,
  PcpFormDataDto,
} from '../types';
import { benefitFactiodMapper } from './constants';
import { PcpMembersDto } from '../../../../queries/useBenefitPlansQuery/types';
import {
  BenefitDto,
  CategoryResponseDto,
} from '../../../../queries/useBenefitCategoryQuery/types';
import {
  calculateAllocation,
  checkEmptyAllocation,
  checkZeroAllocation,
  filterBeneficiaries,
  isCoverageDeclined,
} from 'enrollment/utils/common/Utils';
import { BeneficiaryTypes } from '../../../AddYourBeneficiaries/services';

export const buildElectionParams = ({
  plan,
  levelId,
  orgBenefitId,
  enrollmentContextType,
  selectedDependentIds,
  terminationReasonId,
  contribution,
  eeLumpSum,
  contributionEndDate,
  electedRiders,
  additionalPlanData,
}: BuildElectionParamsDto) => {
  if (!orgBenefitId || !enrollmentContextType) {
    return {} as ElectRequestDto;
  }

  const riderMapping = mapElectedRiders(electedRiders);
  const params = {
    org_benefit_id: orgBenefitId,
    election: {
      org_plan_id: plan.plan_id,
      coverage_level_id: levelId,
      dependent_ids: selectedDependentIds,
      subscriber_amount: contribution ? contribution : plan.subscriber_premium,
      ee_lump_sum_contribution_amount: eeLumpSum ? eeLumpSum : 0,
      hsa_contribution_end_date: contributionEndDate,
    },
    enrollment_context_type: enrollmentContextType,
    termination_reason_id: terminationReasonId,
    ...riderMapping,
  } as ElectRequestDto;

  if (additionalPlanData?.pcpFormData) {
    params.pcp_data = buildPcpDataHash(additionalPlanData.pcpFormData);
  }

  if (additionalPlanData?.esign_checked) {
    params.esign_checked = additionalPlanData.esign_checked;
  }
  if (additionalPlanData?.esign_password) {
    params.esign_password = additionalPlanData.esign_password;
  }
  if (additionalPlanData?.v3_hsa_data) {
    params.v3_hsa_data = additionalPlanData.v3_hsa_data;
  }

  return params;
};

export const mapDefaultFactiods = (benefitLookup: string) => {
  return benefitFactiodMapper.get(benefitLookup);
};

export const validatePcpRequiredFields = (
  pcpFormData: PcpFormDataDto,
  isPcpNameRequired: boolean,
  isPcpCodeRequired: boolean
) => {
  let hasMissingField = false;
  let hasDependents = false;

  const isFilled = (value: string | null | undefined) => value != null && value !== '';

  if (pcpFormData?.dependents) {
    hasDependents = pcpFormData.dependents.length > 0;
  }

  if (isPcpNameRequired && isPcpCodeRequired) {
    if (hasDependents) {
      hasMissingField =
        !isFilled(pcpFormData.pcpName) ||
        !isFilled(pcpFormData.pcpCode) ||
        !pcpFormData?.dependents.every(
          (dep: DependentFormDataDto) => isFilled(dep.pcpName)
        ) ||
        !pcpFormData?.dependents.every(
          (dep: DependentFormDataDto) => isFilled(dep.pcpCode)
        );
    } else {
      hasMissingField =
        !isFilled(pcpFormData.pcpName) || !isFilled(pcpFormData.pcpCode);
    }
  } else if (isPcpCodeRequired) {
    if (hasDependents) {
      hasMissingField =
        !isFilled(pcpFormData.pcpCode) ||
        !pcpFormData?.dependents.every(
          (dep: DependentFormDataDto) => isFilled(dep.pcpCode)
        );
    } else {
      hasMissingField = !isFilled(pcpFormData.pcpCode);
    }
  } else if (isPcpNameRequired) {
    if (hasDependents) {
      hasMissingField =
        !isFilled(pcpFormData.pcpName) ||
        !pcpFormData?.dependents.every(
          (dep: DependentFormDataDto) => isFilled(dep.pcpName)
        );
    } else {
      hasMissingField = !isFilled(pcpFormData.pcpName);
    }
  }
  return hasMissingField;
};

const buildPcpDataHash = (pcpData: PcpFormDataDto) => {
  const pcpDataElectionRequest = {
    pcp_code: pcpData.pcpCode,
    pcp_name: pcpData.pcpName,
    existing_patient: pcpData.existingPatient,
    dependents: pcpData?.dependents && [
      ...pcpData?.dependents.map((depPcpData: DependentFormDataDto) => {
        return {
          id: depPcpData.id,
          pcp_code: depPcpData.pcpCode,
          pcp_name: depPcpData.pcpName,
          existing_patient: depPcpData.existingPatient,
        };
      }),
    ],
  };
  return pcpDataElectionRequest;
};

export const handleCoverageLevelId = (
  planLevelId: number,
  planInfoData?: PlanInfoResponseDto,
  selectedCovLevel?: string,
  parentCovLevel?: string,
  isDepSelect?: boolean
) => {
  let coverageLevelId = planLevelId;
  if (!isDepSelect) {
    const chosenLevel = parentCovLevel || selectedCovLevel;
    const selectedLevel = Object.entries(
      planInfoData?.data?.plan_level_costs as PlanLevelCostDto
    ).find((level) => level[1].level_lookup_code === chosenLevel);

    coverageLevelId = selectedLevel
      ? selectedLevel[1].level_id
      : coverageLevelId;
  }
  return coverageLevelId;
};

export const validatePcpEmployee = (pcpMembers: PcpMembersDto | undefined) => {
  return !_.isEmpty(pcpMembers?.pcp_name) || !_.isEmpty(pcpMembers?.pcp_code);
};

export const validatePcpDependents = (
  pcpMembers: PcpMembersDto | undefined
) => {
  if (pcpMembers?.dependents && pcpMembers.dependents.length > 0) {
    const availableDepPcpData = pcpMembers?.dependents.map(
      (dep) => !_.isEmpty(dep?.pcp_name) || !_.isEmpty(dep?.pcp_code)
    );
    return availableDepPcpData?.includes(true);
  }
  return false;
};

export const getOptionalPcpPlans = (
  benefitPlans: CategoryResponseDto | undefined
) => {
  return benefitPlans?.data?.benefits?.filter(
    (benefit: BenefitDto) =>
      benefit?.coverage?.pcp_data !== null &&
      benefit?.coverage?.pcp_data?.pcp_name_setting === 'optional' &&
      benefit?.coverage?.pcp_data?.pcp_code_setting === 'optional'
  );
};

export const getRequiredPcpPlans = (
  benefitPlans: CategoryResponseDto | undefined
) => {
  return benefitPlans?.data?.benefits?.filter(
    (benefit: BenefitDto) =>
      benefit?.coverage?.pcp_data !== null &&
      benefit?.coverage?.pcp_data?.pcp_name_setting === 'required' &&
      benefit?.coverage?.pcp_data?.pcp_code_setting === 'required'
  );
};

export const getAdditionalPlanInformation = (
  benefitPlans: CategoryResponseDto | undefined
) => {
  return (
    benefitPlans?.data?.benefits?.some(
      (benefit: BenefitDto) =>
        benefit.coverage && benefit.coverage.has_popup_content
    ) || false
  );
};

export const mapElectedRiders = (
  electedRiders: number[] | undefined,
  coverageLineId?: number
) => {
  if (!electedRiders || electedRiders.length < 1) {
    return {};
  }

  const map: { [key: string]: string } = {};
  electedRiders.forEach((riderId) => {
    map[
      `rider_benefit_${riderId}${Number(coverageLineId) >= 0 ? `_${coverageLineId}` : ''}`
    ] = `${riderId}${Number(coverageLineId) >= 0 ? `:${coverageLineId}` : ''}`;
  });
  return map;
};

export const filteredBeneficiaries = (
  data: CategoryResponseDto | undefined
) => {
  return data?.data?.benefits?.map((benefit: BenefitDto) => {
    if (
      benefit?.coverage &&
      !isCoverageDeclined(benefit?.coverage) &&
      benefit?.benefit?.beneficiary_required === true
    ) {
      const beneficiaries: BeneficiaryTypes[] =
        benefit?.coverage?.beneficiaries || [];
      const updatedBeneficiaries = benefit?.coverage?.beneficiaries?.map(
        (beneficiary) => {
          if (beneficiary.id) {
            return { ...beneficiary, uniq_id: beneficiary.id };
          }
          return beneficiary;
        }
      );
      return {
        orgBenefitId: benefit?.benefit?.org_benefit_id,
        orgBenefitName: benefit?.benefit?.org_benefit_name,
        beneficiaries: updatedBeneficiaries,
        primaryTotalAllocation: calculateAllocation(
          filterBeneficiaries(beneficiaries, true)
        ),
        secondaryTotalAllocation: calculateAllocation(
          filterBeneficiaries(beneficiaries, false)
        ),
        primaryEmptyAllocation: checkEmptyAllocation(
          filterBeneficiaries(beneficiaries, true)
        ),
        secondaryEmptyAllocation: checkEmptyAllocation(
          filterBeneficiaries(beneficiaries, false)
        ),
        primaryZeroAllocation: checkZeroAllocation(
          filterBeneficiaries(beneficiaries, true)
        ),
        secondaryZeroAllocation: checkZeroAllocation(
          filterBeneficiaries(beneficiaries, false)
        ),
        coverageFields: {
          req_volume: benefit?.coverage?.req_volume,
          carrier_logo_url: benefit?.coverage?.carrier_logo_url,
          carrier_name: benefit?.coverage?.carrier_name,
          esign_data: benefit?.coverage?.beneficiary_esign_data,
        },
        stackedCoverageLines: benefit?.coverage?.coverage_lines || [],
      };
    }
    return null;
  });
};

export const formatDate = (date: string) => {
  const splitDate = date.split('/');
  return `${splitDate[1]}/${splitDate[2]}/${splitDate[0]}`;
};
